import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import ImportModal from '../common/ImportModal';
import { formatCurrency } from '../../utils/helpers';
import { validators, validateForm, isPartyReferenced } from '../../utils/validation';

const emptyParty = { name: '', type: 'Customer', category: 'General', ledgerGroupId: '', createLedger: true, linkedLedger: '', urdType: 'B2B', currency: 'INR', gstin: '', pan: '', stateGstCode: '27', phone: '', email: '', address: '', shipToAddress: '', state: 'Maharashtra', creditPeriodDays: 0, openingBalance: 0, balanceType: 'Dr' };

const PARTY_CATEGORIES = ['Raw Material', 'Consumable', 'Transporter', 'Services', 'General', 'Other'];
const URD_TYPES = ['B2B', 'B2C', 'B2URD'];
const CURRENCIES = ['INR', 'USD', 'EURO'];

// GST state codes (first 2 digits of GSTIN)
const STATE_GST_CODES = {
  'Jammu & Kashmir': '01', 'Himachal Pradesh': '02', 'Punjab': '03', 'Chandigarh': '04', 'Uttarakhand': '05',
  'Haryana': '06', 'Delhi': '07', 'Rajasthan': '08', 'Uttar Pradesh': '09', 'Bihar': '10', 'Sikkim': '11',
  'Arunachal Pradesh': '12', 'Nagaland': '13', 'Manipur': '14', 'Mizoram': '15', 'Tripura': '16', 'Meghalaya': '17',
  'Assam': '18', 'West Bengal': '19', 'Jharkhand': '20', 'Odisha': '21', 'Chhattisgarh': '22', 'Madhya Pradesh': '23',
  'Gujarat': '24', 'Dadra & Nagar Haveli': '26', 'Maharashtra': '27', 'Karnataka': '29', 'Goa': '30',
  'Lakshadweep': '31', 'Kerala': '32', 'Tamil Nadu': '33', 'Puducherry': '34', 'Andaman & Nicobar': '35',
  'Telangana': '36', 'Andhra Pradesh': '37', 'Ladakh': '38',
};

export default function PartyMaster() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyParty);
  const [editing, setEditing] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const existingNames = useMemo(() =>
    state.parties.map(p => ({ id: p.id, value: p.name })),
    [state.parties]
  );

  const existingGstins = useMemo(() =>
    state.parties.filter(p => p.gstin).map(p => ({ id: p.id, value: p.gstin })),
    [state.parties]
  );

  // Account groups suitable for a party ledger (Sundry Debtors / Creditors and
  // similar). Falls back to all groups if none are tagged that way.
  const ledgerGroups = useMemo(() => {
    const all = state.accountGroups || [];
    const party = all.filter(g => /debtor|creditor|sundry|customer|supplier|receivable|payable/i.test(`${g.accountName || ''} ${g.groupName || ''}`));
    return party.length ? party : all;
  }, [state.accountGroups]);

  // Existing ledgers to link when NOT auto-creating — scoped to the chosen
  // account group (or Sundry Debtors/Creditors when no group is picked yet).
  const linkableLedgers = useMemo(() => {
    if (form.ledgerGroupId) return state.accounts.filter(a => a.groupId === form.ledgerGroupId);
    return state.accounts.filter(a => {
      const g = (a.group || '').toLowerCase();
      return g.includes('sundry debtor') || g.includes('sundry creditor');
    });
  }, [state.accounts, form.ledgerGroupId]);

  const errors = useMemo(() => {
    const { errors } = validateForm({
      name: [
        validators.required(form.name, 'Party name'),
        validators.minLength(form.name, 2, 'Party name'),
        validators.maxLength(form.name, 100, 'Party name'),
        validators.unique(form.name, existingNames, 'Party name', editing),
      ],
      gstin: [
        validators.gstin(form.gstin),
        form.gstin ? validators.unique(form.gstin.toUpperCase(), existingGstins, 'GSTIN', editing) : '',
      ],
      pan: [validators.pan(form.pan)],
      phone: [validators.phone(form.phone)],
      email: [validators.email(form.email)],
      state: [validators.required(form.state, 'State')],
      address: [validators.maxLength(form.address, 500, 'Address')],
      shipToAddress: [validators.maxLength(form.shipToAddress, 500, 'Ship to address')],
      openingBalance: [validators.positiveNumber(form.openingBalance, 'Opening balance')],
      creditPeriodDays: [validators.positiveNumber(form.creditPeriodDays, 'Credit period days')],
      ledgerGroupId: [validators.required(form.ledgerGroupId, 'Account group')],
      // The link is required only when we are NOT auto-creating the ledger.
      linkedLedger: [form.createLedger ? '' : validators.required(form.linkedLedger, 'Party account link ledger')],
    });
    return errors;
  }, [form, existingNames, existingGstins, editing]);

  const hasErrors = Object.values(errors).some(e => e);

  const touch = (field) => setTouched(prev => ({ ...prev, [field]: true }));
  const touchAll = () => {
    const all = {};
    Object.keys(errors).forEach(k => { all[k] = true; });
    setTouched(all);
  };

  const handleSave = () => {
    setSubmitted(true);
    touchAll();
    if (hasErrors) return;

    let payload = { ...form };
    // Auto-create a ledger named after the party (under the chosen account
    // group) and link it. If a ledger with that name already exists, reuse it.
    if (form.createLedger) {
      const norm = (s) => (s || '').trim().toLowerCase();
      const existing = state.accounts.find(a => norm(a.name) === norm(form.name));
      let ledgerId;
      if (existing) {
        ledgerId = existing.id;
      } else {
        const grp = (state.accountGroups || []).find(g => g.id === form.ledgerGroupId);
        const gtext = `${grp?.accountName || ''} ${grp?.groupName || ''}`.toLowerCase();
        const type = gtext.includes('bank') ? 'Bank' : gtext.includes('cash') ? 'Cash' : 'Other';
        ledgerId = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `led_${Date.now()}`;
        dispatch({ type: 'ADD_ACCOUNT', payload: { id: ledgerId, name: form.name.trim(), group: grp?.accountName || '', groupId: form.ledgerGroupId, type, openingBalance: 0, balanceType: 'Dr', balance: 0 } });
      }
      payload = { ...payload, linkedLedger: ledgerId };
    }

    if (editing) {
      dispatch({ type: 'UPDATE_PARTY', payload: { ...payload, id: editing } });
      addToast('Party updated successfully', 'success');
    } else {
      dispatch({ type: 'ADD_PARTY', payload });
      addToast(form.createLedger ? 'Party created — ledger auto-created in Account Master' : 'Party created successfully', 'success');
    }
    closeForm();
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(emptyParty);
    setEditing(null);
    setTouched({});
    setSubmitted(false);
  };

  const handleEdit = (party) => {
    // Existing parties already have a ledger — default to "link" mode and
    // pre-fill the account group from the currently linked ledger.
    const linkedAcc = state.accounts.find(a => a.id === party.linkedLedger);
    setForm({ ...emptyParty, ...party, createLedger: false, ledgerGroupId: party.ledgerGroupId || linkedAcc?.groupId || '' });
    setEditing(party.id);
    setTouched({});
    setSubmitted(false);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const refs = isPartyReferenced(id, state);
    if (refs.length > 0) {
      alert(`Cannot delete: this party is referenced in ${refs.join(', ')}`);
      return;
    }
    const ok = await confirm('Delete this party? This action cannot be undone.', { title: 'Delete Party', variant: 'danger', confirmLabel: 'Delete' });
    if (ok) {
      dispatch({ type: 'DELETE_PARTY', payload: id });
      addToast('Party deleted', 'success');
    }
  };

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'type', label: 'Type', sortable: true, render: (v) => (
      <span className={`badge ${v === 'Customer' ? 'badge-success' : v === 'Supplier' ? 'badge-info' : 'badge-warning'}`}>{v}</span>
    )},
    { key: 'gstin', label: 'GSTIN' },
    { key: 'phone', label: 'Phone' },
    { key: 'state', label: 'State' },
    { key: 'openingBalance', label: 'Opening Bal', align: 'right', render: (v, row) => (
      <span className={row.balanceType === 'Dr' ? 'text-red-600' : 'text-green-600'}>
        {formatCurrency(v)} {row.balanceType}
      </span>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Party Master</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} className="btn btn-secondary flex items-center gap-2">
            <Upload size={16} /> Import
          </button>
          <button onClick={() => { setForm(emptyParty); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
            <Plus size={16} /> Add Party
          </button>
        </div>
      </div>

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="Import Parties"
        dedupeKey="name"
        existing={state.parties.map(p => p.name)}
        columns={[
          { key: 'name', label: 'Name', required: true },
          { key: 'type', label: 'Type' },
          { key: 'category', label: 'Category' },
          { key: 'gstin', label: 'GSTIN' },
          { key: 'pan', label: 'PAN' },
          { key: 'state', label: 'State' },
          { key: 'currency', label: 'Currency' },
          { key: 'phone', label: 'Phone' },
          { key: 'email', label: 'Email' },
          { key: 'address', label: 'Address' },
          { key: 'creditPeriodDays', label: 'Credit Period Days', type: 'number' },
          { key: 'openingBalance', label: 'Opening Balance', type: 'number' },
          { key: 'balanceType', label: 'Balance Type' },
        ]}
        sample={{ name: 'ABC Traders', type: 'Customer', category: 'General', gstin: '27AAAPL1234C1Z5', state: 'Maharashtra', currency: 'INR', phone: '9876543210', openingBalance: 0, balanceType: 'Dr' }}
        onAdd={(p) => dispatch({ type: 'ADD_PARTY', payload: { ...emptyParty, ...p, type: p.type || 'Customer', balanceType: p.balanceType || 'Dr' } })}
      />

      <div className="card">
        <DataTable
          columns={columns}
          data={state.parties}
          searchFields={['name', 'gstin', 'phone', 'type']}
          emptyState={{ title: 'No parties yet', description: 'Add your first customer or supplier.', actionLabel: 'Add Party', onAction: () => { setForm(emptyParty); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); } }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => handleDelete(row.id)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={closeForm} title={editing ? 'Edit Party' : 'Add Party'} size="lg">
        <ErrorSummary errors={errors} show={submitted && hasErrors} />

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Party Name" required error={errors.name} touched={touched.name || submitted} className="col-span-2">
            <input className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} onBlur={() => touch('name')} autoFocus maxLength={100} placeholder="Enter party name" />
          </FormField>

          <FormField label="Type" required>
            <select className="input" value={form.type} onChange={e => setForm({...form, type: e.target.value})}>
              <option>Customer</option>
              <option>Supplier</option>
              <option>Both</option>
            </select>
          </FormField>

          <FormField label="Party Category">
            <select className="input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
              {PARTY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormField>

          <FormField label="Account Group" required error={errors.ledgerGroupId} touched={touched.ledgerGroupId || submitted}
            hint={ledgerGroups.length === 0 ? 'No account groups — add them in Masters → Account Group' : 'Group under which the party ledger sits'}>
            <select className="input" value={form.ledgerGroupId} onChange={e => setForm({ ...form, ledgerGroupId: e.target.value, linkedLedger: '' })} onBlur={() => touch('ledgerGroupId')}>
              <option value="">-- Select Account Group --</option>
              {ledgerGroups.map(g => <option key={g.id} value={g.id}>{g.accountName}</option>)}
            </select>
          </FormField>

          <div className="flex items-center gap-2 self-end pb-2">
            <input id="createLedger" type="checkbox" className="w-4 h-4 cursor-pointer" checked={form.createLedger}
              onChange={e => setForm({ ...form, createLedger: e.target.checked, linkedLedger: '' })} />
            <label htmlFor="createLedger" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
              Create Ledger Account (same as party name)
            </label>
          </div>

          {form.createLedger ? (
            <FormField label="Party Account Link Ledger" className="col-span-2" hint="A ledger will be auto-created in Account Master when you save">
              <input className="input bg-gray-100 text-gray-500" value={form.name ? `Auto-create: ${form.name}` : 'Auto-create from party name'} readOnly tabIndex={-1} />
            </FormField>
          ) : (
            <FormField label="Party Account Link Ledger" required error={errors.linkedLedger} touched={touched.linkedLedger || submitted} className="col-span-2"
              hint={form.ledgerGroupId ? 'Ledgers under the selected account group' : 'Select an account group first, or a Sundry Debtors/Creditors ledger'}>
              <select className="input" value={form.linkedLedger} onChange={e => setForm({ ...form, linkedLedger: e.target.value })} onBlur={() => touch('linkedLedger')}>
                <option value="">-- Select Account --</option>
                {linkableLedgers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </FormField>
          )}

          <FormField label="URD Type">
            <select className="input" value={form.urdType} onChange={e => setForm({...form, urdType: e.target.value})}>
              {URD_TYPES.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </FormField>

          <FormField label="Currency">
            <select className="input" value={form.currency} onChange={e => setForm({...form, currency: e.target.value})}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </FormField>

          <FormField label="GSTIN" error={errors.gstin} touched={touched.gstin || submitted} hint="Format: 27AADCA1234B1Z5">
            <input className="input" value={form.gstin} onChange={e => {
              const g = e.target.value.toUpperCase();
              // Auto-derive PAN (chars 3-12) and State GST Code (chars 1-2) from a full GSTIN
              const patch = { gstin: g };
              if (g.length === 15) { patch.stateGstCode = g.slice(0, 2); patch.pan = g.slice(2, 12); }
              setForm({ ...form, ...patch });
            }} onBlur={() => touch('gstin')} maxLength={15} placeholder="27XXXXX1234X1Z5" style={{ textTransform: 'uppercase' }} />
          </FormField>

          <FormField label="PAN No" error={errors.pan} touched={touched.pan || submitted} hint="Format: AAAPL1234C">
            <input className="input" value={form.pan} onChange={e => setForm({...form, pan: e.target.value.toUpperCase()})} onBlur={() => touch('pan')} maxLength={10} placeholder="AAAPL1234C" style={{ textTransform: 'uppercase' }} />
          </FormField>

          <FormField label="State GST Code" hint="2-digit GST state code">
            <input className="input" value={form.stateGstCode} onChange={e => setForm({...form, stateGstCode: e.target.value.replace(/[^0-9]/g, '').slice(0, 2)})} maxLength={2} placeholder="27" />
          </FormField>

          <FormField label="Phone" error={errors.phone} touched={touched.phone || submitted}>
            <input className="input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value.replace(/[^0-9+\-\s()]/g, '')})} onBlur={() => touch('phone')} maxLength={15} placeholder="9876543210" />
          </FormField>

          <FormField label="Email" error={errors.email} touched={touched.email || submitted}>
            <input className="input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} onBlur={() => touch('email')} maxLength={100} placeholder="email@example.com" />
          </FormField>

          <FormField label="State" required error={errors.state} touched={touched.state || submitted}>
            <select className="input" value={form.state} onChange={e => setForm({...form, state: e.target.value, stateGstCode: STATE_GST_CODES[e.target.value] || form.stateGstCode})} onBlur={() => touch('state')}>
              <option value="">-- Select State --</option>
              {['Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Jammu & Kashmir','Ladakh','Chandigarh','Puducherry','Lakshadweep','Andaman & Nicobar','Dadra & Nagar Haveli'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Address" error={errors.address} touched={touched.address || submitted} className="col-span-2">
            <textarea className="input" rows={2} value={form.address} onChange={e => setForm({...form, address: e.target.value})} onBlur={() => touch('address')} maxLength={500} placeholder="Full address" />
          </FormField>

          <FormField label="Ship To Address" error={errors.shipToAddress} touched={touched.shipToAddress || submitted} hint="Optional — used on delivery documents if different" className="col-span-2">
            <textarea className="input" rows={2} value={form.shipToAddress} onChange={e => setForm({...form, shipToAddress: e.target.value})} onBlur={() => touch('shipToAddress')} maxLength={500} placeholder="Shipping address (leave blank if same as above)" />
          </FormField>

          <FormField label="Credit Period (Days)" error={errors.creditPeriodDays} touched={touched.creditPeriodDays || submitted} hint="Payment due days after invoice date">
            <input className="input" type="number" min="0" step="1" value={form.creditPeriodDays} onChange={e => setForm({...form, creditPeriodDays: Number(e.target.value)})} onBlur={() => touch('creditPeriodDays')} placeholder="e.g. 30" />
          </FormField>

          <FormField label="Opening Balance" error={errors.openingBalance} touched={touched.openingBalance || submitted}>
            <input className="input" type="number" min="0" step="0.01" value={form.openingBalance} onChange={e => setForm({...form, openingBalance: Number(e.target.value)})} onBlur={() => touch('openingBalance')} />
          </FormField>

          <FormField label="Balance Type">
            <select className="input" value={form.balanceType} onChange={e => setForm({...form, balanceType: e.target.value})}>
              <option value="Dr">Debit (Dr) - They Owe Us</option>
              <option value="Cr">Credit (Cr) - We Owe Them</option>
            </select>
          </FormField>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeForm} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={submitted && hasErrors}>
            {editing ? 'Update' : 'Save'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
